import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FileSpreadsheet, FileText, Loader2, RefreshCcw, Search, SlidersHorizontal } from 'lucide-react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import OrderAttachmentModal from '../components/OrderAttachmentModal';
import { fetchOrderStatusReport } from '../services/reportService';

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
});

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return currencyFormatter.format(0);
  return currencyFormatter.format(numeric);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const STATUS_OPTIONS = [
  { value: 'delivered', label: 'Delivered (Completed Orders)' },
  { value: 'pending', label: 'Pending (All non-delivered)' },
  { value: 'all', label: 'All Statuses' },
  { value: '1', label: 'Placed' },
  { value: '2', label: 'Informed to Shop' },
  { value: '3', label: 'Ready for Pickup' },
  { value: '4', label: 'Delivery Boy Assigned' },
  { value: '5', label: 'Picked Up' },
  { value: '6', label: 'Delivered' },
  { value: '7', label: 'Cancelled' },
];

const DEFAULT_PAGE_SIZE = 20;

export default function OrderStatusReport() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({ totalOrders: 0, totalAmount: 0, totalDeliveryCharge: 0, totalFinalAmount: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('delivered');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateField, setDateField] = useState('deliveredAt');
  const [loading, setLoading] = useState(false);
  const [csvExporting, setCsvExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [attachmentOrder, setAttachmentOrder] = useState(null);

  const exportColumns = useMemo(
    () => [
      {
        header: 'Order ID',
        getExcelValue: (order) => order.showId || '',
        getPdfValue: (order) => order.showId || '',
      },
      {
        header: 'Status',
        getExcelValue: (order) => order.currentStatus?.name || '',
        getPdfValue: (order) => order.currentStatus?.name || '',
      },
      {
        header: 'Customer',
        getExcelValue: (order) => order.customer?.name || '',
        getPdfValue: (order) => order.customer?.name || '',
      },
      {
        header: 'Phone',
        getExcelValue: (order) => order.customer?.phone || '',
        getPdfValue: (order) => order.customer?.phone || '',
      },
      {
        header: 'Total Amount',
        getExcelValue: (order) => Number(order.totalAmount || 0),
        getPdfValue: (order) => formatCurrency(order.totalAmount),
      },
      {
        header: 'Delivery Charge',
        getExcelValue: (order) => Number(order.deliveryCharge || 0),
        getPdfValue: (order) => formatCurrency(order.deliveryCharge),
      },
      {
        header: 'Final Amount',
        getExcelValue: (order) => Number(order.finalAmount || 0),
        getPdfValue: (order) => formatCurrency(order.finalAmount),
      },
      {
        header: 'Created At',
        getExcelValue: (order) => formatDateTime(order.createdAt),
        getPdfValue: (order) => formatDateTime(order.createdAt),
      },
      {
        header: 'Delivered At',
        getExcelValue: (order) => formatDateTime(order.deliveredAt),
        getPdfValue: (order) => formatDateTime(order.deliveredAt),
      },
      {
        header: 'Shops',
        getExcelValue: (order) => order.shops?.length || 0,
        getPdfValue: (order) => order.shops?.length || 0,
      },
    ],
    [],
  );

  const queryParams = useMemo(() => {
    const params = {
      page,
      limit: DEFAULT_PAGE_SIZE,
      dateField,
    };
    const trimmedSearch = search.trim();
    if (trimmedSearch) params.search = trimmedSearch;
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return params;
  }, [dateField, endDate, page, search, startDate, statusFilter]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await fetchOrderStatusReport(queryParams);
      const data = response.data?.data || {};
      setOrders(data.records || []);
      setSummary({
        totalOrders: data.summary?.totalOrders || 0,
        totalAmount: data.summary?.totalAmount || 0,
        totalDeliveryCharge: data.summary?.totalDeliveryCharge || 0,
        totalFinalAmount: data.summary?.totalFinalAmount || 0,
      });
      setTotalPages(data.pages || 1);
    } catch (error) {
      toast.error('Failed to load order report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPage(1);
    if (value === 'delivered' || value === '6') {
      setDateField('deliveredAt');
    } else if (dateField === 'deliveredAt') {
      setDateField('createdAt');
    }
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleDateFieldChange = (event) => {
    setDateField(event.target.value);
    setPage(1);
  };

  const handleDateChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  const fetchExportRecords = async () => {
    const exportParams = { ...queryParams, export: true, page: 1 };
    const response = await fetchOrderStatusReport(exportParams);
    return response.data?.data?.records || [];
  };

  const formatCsvValue = (value) => {
    if (value == null) return '';
    const stringValue = String(value);
    const escapedValue = stringValue.replace(/"/g, '""');
    return /[",\r\n]/.test(stringValue) ? `"${escapedValue}"` : escapedValue;
  };

  const buildCsv = (rows) =>
    rows
      .map((row) => row.map((value) => formatCsvValue(value ?? '')).join(','))
      .join('\r\n');

  const handleExportCsv = async () => {
    setCsvExporting(true);
    try {
      const rows = await fetchExportRecords();
      if (!rows.length) {
        toast.info('No records available to export');
        return;
      }
      const header = exportColumns.map((column) => column.header);
      const body = rows.map((order) =>
        exportColumns.map((column) => {
          const value = column.getExcelValue(order);
          return value ?? '';
        }),
      );
      const csvContent = buildCsv([header, ...body]);
      const blob = new Blob(['\ufeff', csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'order-status-report.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setCsvExporting(false);
    }
  };

  const escapeHtml = (value) => {
    const stringValue = String(value ?? '');
    return stringValue
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const handleExportPdf = async () => {
    setPdfExporting(true);
    try {
      const rows = await fetchExportRecords();
      if (!rows.length) {
        toast.info('No records available to export');
        return;
      }
      const tableHead = `<tr>${exportColumns
        .map((column) => `<th>${escapeHtml(column.header)}</th>`)
        .join('')}</tr>`;
      const tableBody = rows
        .map(
          (order) =>
            `<tr>${exportColumns
              .map((column) => `<td>${escapeHtml(column.getPdfValue(order))}</td>`)
              .join('')}</tr>`,
        )
        .join('');
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow pop-ups to export PDF');
        return;
      }
      printWindow.document.write(`
        <html>
          <head>
            <title>Order Status Report</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 24px; color: #111827; }
              h2 { margin-bottom: 16px; }
              table { border-collapse: collapse; width: 100%; }
              th, td { border: 1px solid #d1d5db; padding: 6px 8px; font-size: 12px; text-align: left; }
              th { background: #e5e7eb; }
              tr:nth-child(even) { background: #f9fafb; }
            </style>
          </head>
          <body>
            <h2>Order Status Report</h2>
            <p>Generated on ${new Date().toLocaleString()}</p>
            <table>
              <thead>${tableHead}</thead>
              <tbody>${tableBody}</tbody>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setPdfExporting(false);
    }
  };

  const openAttachments = (order) => {
    setAttachmentOrder(order);
  };

  const closeAttachments = () => setAttachmentOrder(null);

  return (
    <LayoutWithSidebar>
      <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Order Status Report</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Monitor delivered and pending orders with quick export to CSV or PDF.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadReport}
              className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50"
              disabled={loading}
            >
              {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCcw size={16} className="mr-2" />}
              Refresh
            </button>
            <button
              type="button"
              onClick={handleExportCsv}
              className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-500"
              disabled={csvExporting}
            >
              {csvExporting ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet size={16} className="mr-2" />
              )}
              Export CSV
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-500"
              disabled={pdfExporting}
            >
              {pdfExporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileText size={16} className="mr-2" />}
              Export PDF
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by order ID"
                className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <div className="flex items-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                <SlidersHorizontal size={14} className="mr-2" />
                Status
              </div>
              <select
                value={statusFilter}
                onChange={(event) => handleStatusChange(event.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Start Date</div>
              <input
                type="date"
                value={startDate}
                onChange={handleDateChange(setStartDate)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">End Date</div>
              <input
                type="date"
                value={endDate}
                onChange={handleDateChange(setEndDate)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Date Field</div>
              <select
                value={dateField}
                onChange={handleDateFieldChange}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="createdAt">Order Created</option>
                <option value="deliveredAt">Order Delivered</option>
              </select>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-100 dark:border-indigo-800 rounded-xl p-4">
              <p className="text-xs text-indigo-600 dark:text-indigo-300 uppercase tracking-wide">Total Orders</p>
              <p className="text-xl font-semibold text-indigo-700 dark:text-indigo-200">{summary.totalOrders}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/30 border border-green-100 dark:border-green-800 rounded-xl p-4">
              <p className="text-xs text-green-600 dark:text-green-300 uppercase tracking-wide">Delivery Charges</p>
              <p className="text-xl font-semibold text-green-700 dark:text-green-200">{formatCurrency(summary.totalDeliveryCharge)}</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-100 dark:border-blue-800 rounded-xl p-4">
              <p className="text-xs text-blue-600 dark:text-blue-300 uppercase tracking-wide">Order Value</p>
              <p className="text-xl font-semibold text-blue-700 dark:text-blue-200">{formatCurrency(summary.totalAmount)}</p>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800 rounded-xl p-4">
              <p className="text-xs text-emerald-600 dark:text-emerald-300 uppercase tracking-wide">Final Amount</p>
              <p className="text-xl font-semibold text-emerald-700 dark:text-emerald-200">{formatCurrency(summary.totalFinalAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-800/60">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Order ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Delivery</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Final</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Delivered</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Shops</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
                {loading ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-10 text-center text-sm text-gray-500">
                      <Loader2 size={24} className="mx-auto animate-spin text-indigo-500" />
                      <p className="mt-2">Loading report...</p>
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-10 text-center text-sm text-gray-500">
                      No orders found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{order.showId}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{order.currentStatus?.name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        <div>{order.customer?.name || '-'}</div>
                        <div className="text-xs text-gray-500">{order.customer?.phone || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(order.totalAmount)}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(order.deliveryCharge)}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(order.finalAmount)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{formatDateTime(order.createdAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{formatDateTime(order.deliveredAt)}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-700 dark:text-gray-300">{order.shops?.length || 0}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        <button
                          type="button"
                          onClick={() => openAttachments(order)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full border border-indigo-100 dark:border-indigo-700 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-800/40"
                        >
                          View Attachments
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/60 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1 || loading}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages || loading}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
      {attachmentOrder && <OrderAttachmentModal order={attachmentOrder} onClose={closeAttachments} />}
    </LayoutWithSidebar>
  );
}
